import React, { FC } from 'react';
import { LabeledInput } from './LabeledInput';
import styles from './Input.module.css';

export enum InputTypes {
    text = 'text',
    email = 'email',
    number = 'number',
    date = 'date',
    tel = 'tel',
    time = 'time',
    password = 'password',
    file = 'file',
    textarea = 'textarea',
}

export enum InputVariants {
    regular = 'regular',
    labeledInput = 'labeled-input',
    searchInput = 'search-input',
}

export enum InputStates {
    error = 'error',
    success = 'success',
}

export enum InputCheckRules {
    passwordBuild = 'password-build',
}

export interface InputProps {
    id?: string;
    classes?: string;
    type: InputTypes;
    size?: 'sm' | 'md';
    align?: 'left' | 'center';
    variant?: InputVariants;
    label?: string;
    placeholder?: string;
    min?: number;
    max?: number;
    step?: number;
    maxLength?: number;
    value: string;
    onChange: (value: any) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    onInput?: (value: any) => void;
    state?: InputStates;
    errorMessages?: string[];
    checkRules?: InputCheckRules;
    onValidationChange?: (isValid: boolean) => void;
    disabled?: boolean;
    readOnly?: boolean;
    multiple?: boolean;
}

export const Input: FC<InputProps> = ({
    id,
    classes,
    type,
    size,
    align,
    variant,
    label,
    placeholder,
    min,
    max,
    step,
    value,
    onChange,
    onInput,
    onKeyDown,
    maxLength,
    state,
    errorMessages,
    checkRules,
    onValidationChange,
    disabled,
    readOnly,
    multiple,
}) => {
    return (
        <>
            {variant === InputVariants.labeledInput ? (
                <LabeledInput
                    id={id || undefined}
                    classes={classes}
                    type={type}
                    label={label || ''}
                    min={min}
                    max={max}
                    step={step}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onInput={onInput}
                    maxLength={maxLength}
                    state={state}
                    errorMessages={errorMessages}
                    checkRules={checkRules}
                    onValidationChange={onValidationChange}
                    disabled={disabled}
                    readOnly={readOnly}
                    multiple={type === InputTypes.file && multiple}
                />
            ) : variant === InputVariants.searchInput ? (
                <SearchInput
                    id={id || undefined}
                    classes={classes}
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onKeyDown={(e) => {
                        if (onKeyDown) {
                            onKeyDown(e);
                        }
                    }}
                    disabled={disabled}
                    readOnly={readOnly}
                />
            ) : type === InputTypes.textarea ? (
                <div
                    className={
                        styles['regular-input'] +
                        // state
                        ` ${state === 'error' ? styles['has-error'] : state === 'success' ? styles.success : ''}`
                    }>
                    <textarea
                        id={id || undefined}
                        className={
                            `form-control ${styles['form-control']}` +
                            // size
                            ` ${size === 'sm' ? `form-control-sm ${styles['form-control-sm']}` : ''}` +
                            // align
                            ` ${align === 'center' ? 'text-center' : ''}` +
                            // extra classes
                            ` ${classes || ''}`
                        }
                        placeholder={placeholder}
                        onChange={(e) => onChange(e.target.value)}
                        disabled={disabled}
                        readOnly={readOnly}
                        value={value}
                        data-testid="input"
                    />
                    {state === 'error' && <i className="fa-light fa-triangle-exclamation" />}
                    {state === 'success' && <i className="fa-light fa-circle-check" />}
                </div>
            ) : (
                <div
                    className={
                        styles['regular-input'] +
                        // state
                        ` ${state === 'error' ? styles['has-error'] : state === 'success' ? styles.success : ''}`
                    }>
                    <input
                        id={id || undefined}
                        className={
                            `form-control ${styles['form-control']}` +
                            // size
                            ` ${size === 'sm' ? `form-control-sm ${styles['form-control-sm']}` : ''}` +
                            // align
                            ` ${align === 'center' ? 'text-center' : ''}` +
                            // extra classes
                            ` ${classes || ''}`
                        }
                        type={type}
                        placeholder={placeholder}
                        min={min}
                        max={max}
                        step={type === InputTypes.number ? step || 'any' : undefined}
                        value={type === InputTypes.file ? undefined : value}
                        onChange={(e) => {
                            if (type === InputTypes.file) {
                                onChange(e.target.files);
                            } else {
                                onChange(e.target.value);
                            }
                        }}
                        onInput={onInput}
                        maxLength={maxLength}
                        disabled={disabled}
                        readOnly={readOnly}
                        multiple={type === InputTypes.file && multiple}
                        data-testid="input"
                    />
                    {state === 'error' && <i className="fa-light fa-triangle-exclamation" />}
                    {state === 'success' && <i className="fa-light fa-circle-check" />}
                </div>
            )}
        </>
    );
};

export interface SearchInputProps {
    id?: string;
    classes?: string;
    type: InputTypes;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    readOnly?: boolean;
}

const SearchInput: FC<SearchInputProps> = ({
    id,
    classes,
    type,
    placeholder,
    value,
    onChange,
    onKeyDown,
    disabled,
    readOnly,
}) => {
    return (
        <div className={`${styles['search-input']} ${classes || ''}`}>
            <i className="fa-light fa-magnifying-glass" />
            <input
                id={id || undefined}
                type={type}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (onKeyDown) {
                        onKeyDown(e);
                    }
                }}
                disabled={disabled}
                readOnly={readOnly}
                data-testid="input"
            />
        </div>
    );
};
